import React, {
  RefObject,
  useCallback,
  DragEvent,
  memo,
  useMemo,
} from 'react';
import Controller from 'core/components/FormController';
import {
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import clsx from 'clsx';
import TextArea from 'core/components/TextArea';
import {
  VisitNoteSOAPSectionNames,
} from 'core/constants';
import {
  fieldChangedCSS,
  visitNoteEditorActions,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/constants';

type Props = {
  editable: boolean;
  sectionRef?: RefObject<HTMLDivElement>;
};

type SectionProps = {
  editable: boolean;
  sectionRef?: RefObject<HTMLDivElement>;
  field: FieldValues;
  isDirty: boolean;
};

function PhysicalExamSection({
  editable,
  sectionRef,
  field,
  isDirty,
}: SectionProps) {
  const textAreaClasses = useMemo(() => ({
    root: clsx({
      [fieldChangedCSS]: isDirty && editable,
    }),
  }), [isDirty, editable]);

  const label = useMemo(() => ((editable || (!editable && (field.value ?? '').length > 0)) ? 'Objective: Physical Exam' : ''), [editable, field.value]);
  const wrapperClasses = useMemo(() => (clsx({
    '!mt-0': !editable && field.value?.length === 0,
  })), [editable, field.value]);

  const blockEditorDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'none';
  }, []);

  return (
    <Wrapper
      id="physicalExam"
      wrapperRef={sectionRef}
      className={wrapperClasses}
      label={label}
    >
      <TextArea
        {...field}
        trackingLabel="Physical Exam"
        id={VisitNoteSOAPSectionNames.PhysicalExamTemplate}
        classes={textAreaClasses}
        disabled={!editable}
        actions={visitNoteEditorActions}
        onDragOver={blockEditorDrop}
      />
    </Wrapper>
  );
}

const PhysicalExamSectionMemoized = memo(PhysicalExamSection);

function PhysicalExam({
  editable,
  sectionRef,
}: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name="physicalExam"
      control={control}
      render={({ field, fieldState: { isDirty } }) => (
        <PhysicalExamSectionMemoized
          editable={editable}
          sectionRef={sectionRef}
          field={field}
          isDirty={isDirty}
        />
      )}
    />
  );
}

export default memo(PhysicalExam);
